import React, { useState, useCallback, useEffect } from "react";
// Import necessary modules...
import { Order, userCancelOrder } from "../api/order";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Check, ListOrdered } from "lucide-react"; // Ajout de ListOrdered (optionnel)
import { useWebSocket } from "../api/useWebSocket";
import { getItem, removeItem } from "../utils/localStorage";
import Checkout from "./Checkout"; // Assuming Checkout component is in the same directory.
import ConfirmationModal from "./ConfirmationModal";
import { toast } from "sonner";
import {
  calculateDistance,
  isTokenExpired,
  MAX_SPEED,
  MIN_SPEED,
  WS_ORDER_TOPIC,
  WS_SERVER_URL,
} from "../utils/utils";
import { refreshAccessToken } from "../api/auth";
import { LocationSettings } from "../types";
import { getLocationSettings } from "../api/settings";
import { use } from "i18next";

interface OrderDetailProps {
  order: Order;
  onBack: () => void;
}

const getStatusColor = (status: string) => {
  switch (status) {
    case "DELIVERED":
      return "bg-green-100 text-green-800";
    case "CANCELLED":
      return "bg-red-100 text-red-800";
    case "OUT_FOR_DELIVERY":
      return "bg-blue-100 text-blue-800";
    case "PENDING":
    case "PAID":
    case "PREPARING":
      return "bg-yellow-100 text-yellow-800";
    case "CREATED":
    default:
      return "bg-gray-100 text-gray-800";
  }
};

const OrderDetail: React.FC<OrderDetailProps> = ({ order, onBack }) => {
  const { t } = useTranslation();
  const [currentOrder, setCurrentOrder] = useState<Order>(order);
  const [isPaymentModalOpen, setPaymentModalOpen] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [isConfirmationOpen, setConfirmationOpen] = useState(false);
  const [estimatedDistance, setEstimatedDistance] = useState(0);
  const [locationSettings, setLocationSettings] =
    useState<LocationSettings | null>(null);

  const statusTimeline =
    currentOrder.type === "DELIVERY"
      ? ["CREATED", "PAID", "PREPARING", "OUT_FOR_DELIVERY", "DELIVERED"]
      : ["CREATED", "PAID", "PREPARING", "DELIVERED"];
  const user = getItem("user") as { id: string } | undefined;

  const token = getItem("authToken") as string | undefined;
  const [accessToken, setAccessToken] = useState<string | undefined>(token);

  const handleOrderUpdate = useCallback(
    (updatedOrder: Order) => {
      setCurrentOrder(updatedOrder);
      console.log("Order update received:", updatedOrder);
    },
    [currentOrder]
  );

  console.log("Current Order in Detail:", currentOrder);

  const currentStatusIndex = statusTimeline.indexOf(
    currentOrder.status.toUpperCase()
  );

  const handleGetToken = useCallback(async () => {
    if (!accessToken) return undefined;

    if (isTokenExpired(accessToken)) {
      try {
        const freshToken = await refreshAccessToken();
        setAccessToken(freshToken.data?.accessToken);
        return freshToken.data?.accessToken;
      } catch (error: any) {
        toast.error(error.error.message);
        setAccessToken(undefined);
        return undefined;
      }
    }
    // Si le token est toujours valide
    return accessToken;
  }, [accessToken]);

  const { status } = useWebSocket<Order>({
    serverUrl: WS_SERVER_URL,
    topic: WS_ORDER_TOPIC + user?.id,
    onMessage: handleOrderUpdate,
    getToken: handleGetToken,
    debug: true,
  });

  const handlePayment = () => {
    setPaymentModalOpen(true);
  };

  const handleCancelOrder = async () => {
    setConfirmationOpen(false);
    setIsCancelling(true);
    const response = await userCancelOrder(currentOrder.id);
    if (response.error) {
      toast.error(response.error.message);
      return;
    } else if (response.data) {
      setCurrentOrder(response.data);
      toast.success(
        t("orderDetail.cancelSuccess", "Order cancelled successfully")
      );
    }
    setIsCancelling(false);
    removeItem("currentOrderID");
  };

  // Check if the order can be paid
  const canPayOrder =
    new Date(currentOrder.createdAt) <= new Date() &&
    currentOrder.status === "CREATED";
  const canCancelOrder = currentOrder.status === "CREATED";

  useEffect(() => {
    const fetchLocationSettings = async () => {
      const { data, error } = await getLocationSettings();
      console.log("fetchLocationSettings", data);
      if (data) {
        setLocationSettings(data);
      } else {
        console.error("Failed to fetch delivery settings:", error);
        toast.error(error?.message);
      }
    };

    fetchLocationSettings();
  }, [t]);

  useEffect(() => {
    const fetchEstimatedDistance = async () => {
      if (currentOrder.type !== "DELIVERY") return;

      const restaurantCoords = {
        latitude: locationSettings?.latitude || 0,
        longitude: locationSettings?.longitude || 0,
      };
      const destinationCoords = {
        latitude: currentOrder.address.latitude,
        longitude: currentOrder.address.longitude,
      };

      try {
        const distanceInMeters = await calculateDistance(
          restaurantCoords,
          destinationCoords
        );
        if (distanceInMeters !== null) {
          const distanceInKm = distanceInMeters / 1000;
          const newEstimatedDistance = Math.round(distanceInKm * 10) / 10;
          setEstimatedDistance(newEstimatedDistance);
        }
      } catch (error) {
        console.error("Distance calculation failed:", error);
        toast.error(t("cart.errors.distance", "Could not calculate distance."));
      }
    };

    fetchEstimatedDistance();
  }, [currentOrder.address, locationSettings]);

  return (
    <>
      <ConfirmationModal
        isOpen={isConfirmationOpen}
        onClose={() => setConfirmationOpen(false)}
        onConfirm={handleCancelOrder}
        title={t("orderDetail.cancelOrderTitle")}
        message={t("orderDetail.cancelOrderMessage")}
      />
      {/* Main order detail - hidden while payment modal is open */}
      <div className="bg-white shadow-lg rounded-xl p-6 lg:p-8">
        <button
          onClick={onBack}
          className="flex items-center space-x-2 text-gray-600 hover:text-orange-500 mb-6"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>{t("orderDetail.backToOrderList", "Back to order list")}</span>
        </button>

        <div className="flex justify-between items-start mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-800">
              {t("orderDetail.order")} #{currentOrder.id.substring(0, 8)}
            </h2>
            <p className="text-sm text-gray-500">
              {new Date(currentOrder.createdAt).toLocaleString()}
            </p>
          </div>
          <div
            className={`px-3 py-1 text-sm font-semibold rounded-full ${getStatusColor(
              currentOrder.status
            )}`}
          >
            {currentOrder.status === "DELIVERED" &&
            currentOrder.type === "PICKUP"
              ? t(`orderStatus.ready`)
              : t(
                  `orderStatus.${currentOrder.status.toLowerCase()}`,
                  currentOrder.status
                )}
          </div>
        </div>

        {/* Add Payment Button if applicable */}
        {/* {canPayOrder && status === "Connected" && (
          <button
            onClick={handlePayment}
            className="mt-4 px-4 py-2 bg-orange-500 text-white rounded-lg"
          >
            {t("orderDetail.payOrder", "Pay Order")}
          </button>
        )} */}

        {/* {canCancelOrder && status === "Connected" && (
          <button
            onClick={() => setConfirmationOpen(true)}
            disabled={isCancelling}
            className="mt-4 ml-4 px-4 py-2 bg-red-500 text-white rounded-lg disabled:bg-red-300"
          >
            {isCancelling
              ? t("orderDetail.cancelling", "Cancelling...")
              : t("orderDetail.cancelOrder", "Cancel Order")}
          </button>
        )} */}

        {/* 1. Chronologie Horizontale (Desktop/Tablet) */}
        {currentStatusIndex !== -1 && (
          <div className="my-8 hidden md:block">
            <div className="flex items-center">
              {statusTimeline.map((status, index) => (
                <React.Fragment key={status}>
                  <div className="flex flex-col items-center">
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center z-10 ${
                        index <= currentStatusIndex
                          ? "bg-orange-500 text-white"
                          : "bg-gray-200 text-gray-500"
                      }`}
                    >
                      {index < currentStatusIndex ? (
                        <Check className="w-5 h-5" />
                      ) : (
                        <div className="w-3 h-3 bg-white rounded-full" />
                      )}
                    </div>
                    <p
                      className={`mt-2 text-xs text-center font-semibold w-24 ${
                        index <= currentStatusIndex
                          ? "text-orange-500"
                          : "text-gray-500"
                      }`}
                    >
                      {status === "DELIVERED" && currentOrder.type === "PICKUP"
                        ? t(`orderStatus.ready`)
                        : t(`orderStatus.${status.toLowerCase()}`)}
                    </p>
                  </div>
                  {index < statusTimeline.length - 1 && (
                    <div
                      className={`flex-1 h-1 -mx-1 ${
                        index < currentStatusIndex
                          ? "bg-orange-500"
                          : "bg-gray-200"
                      }`}
                    ></div>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        )}

        {/* 2. Chronologie Verticale (Mobile) */}
        {currentStatusIndex !== -1 && (
          <div className="my-8 md:hidden">
            <h3 className="text-lg font-bold text-gray-800 mb-4">
              {t("orderDetail.orderProgress", "Order Progress")}
            </h3>
            <div className="relative border-l-2 border-gray-200 ml-2">
              {statusTimeline.map((status, index) => {
                const isCurrent = index === currentStatusIndex;
                const isCompleted = index < currentStatusIndex;
                // const isFuture = index > currentStatusIndex; // Non utilisé, mais peut être utile

                return (
                  <div key={status} className="mb-8 ml-6">
                    <div
                      className={`absolute w-4 h-4 rounded-full -left-2 top-0 flex items-center justify-center ${
                        isCompleted
                          ? "bg-orange-500"
                          : isCurrent
                          ? "bg-orange-500 border-2 border-white shadow-md"
                          : "bg-gray-200"
                      }`}
                    >
                      {isCompleted && <Check className="w-3 h-3 text-white" />}
                      {isCurrent && (
                        <span className="w-2 h-2 rounded-full bg-white animate-pulse" />
                      )}
                    </div>
                    <p
                      className={`font-semibold ${
                        isCompleted
                          ? "text-gray-500"
                          : isCurrent
                          ? "text-orange-500"
                          : "text-gray-400"
                      }`}
                    >
                      {t(`orderStatus.${status.toLowerCase()}`, status)}
                    </p>
                    {isCurrent && (
                      <p className="text-xs text-orange-400 mt-1">
                        {t("orderDetail.currentStatus", "Current Status")}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Items List */}
        <div className="mb-6">
          <h3 className="text-lg font-bold text-gray-800 mb-4">
            {t("orderDetail.items", "Items")}
          </h3>
          <div className="space-y-4">
            {currentOrder.items.map((item) => (
              <div
                key={item.dish.id}
                className="flex items-center justify-between"
              >
                <div className="flex items-center">
                  <img
                    src={item.dish.imageUrl}
                    alt={item.dish.name}
                    className="w-16 h-16 rounded-lg object-cover mr-4"
                  />
                  <div>
                    <p className="font-semibold text-gray-800">
                      {item.dish.name}
                    </p>
                    <p className="text-sm text-gray-500">
                      {t("orderDetail.quantity")}: {item.quantity}
                    </p>
                  </div>
                </div>
                <p className="font-semibold text-gray-800">
                  {(item.dish.price * item.quantity).toLocaleString(undefined, {
                    style: "currency",
                    currency: "CAD",
                  })}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Pricing */}
        <div className="border-t pt-6">
          <div className="space-y-2">
            <div className="flex justify-between">
              <p className="text-gray-600">
                {t("orderDetail.subtotal", "Subtotal")}
              </p>
              <p className="font-semibold text-gray-800">
                {currentOrder.itemsTotalPrice.toLocaleString(undefined, {
                  style: "currency",
                  currency: "CAD",
                })}
              </p>
            </div>
            <div className="flex justify-between">
              <p className="text-gray-600">
                {t("orderDetail.deliveryFee", "Delivery Fee")}
              </p>
              <p className="font-semibold text-gray-800">
                {currentOrder.deliveryFee.toLocaleString(undefined, {
                  style: "currency",
                  currency: "CAD",
                })}
              </p>
            </div>
            <div className="flex justify-between text-lg font-bold">
              <p className="text-gray-800">{t("orderDetail.total", "Total")}</p>
              <p className="text-orange-600">
                {currentOrder.totalPrice.toLocaleString(undefined, {
                  style: "currency",
                  currency: "CAD",
                })}
              </p>
            </div>
          </div>
        </div>

        {/* Delivery Info */}
        <div className="border-t pt-6 mt-6">
          <h3 className="text-lg font-bold text-gray-800 mb-4">
            {t("orderDetail.deliveryInfo", "Delivery Information")}
          </h3>
          {currentOrder.type === "DELIVERY" && (
            <p className="font-semibold mb-1">
              {t("orderDetail.estimateDeliveryTime")}
            </p>
          )}
          {currentOrder.type === "DELIVERY" ? (
            estimatedDistance !== 0 ? (
              <p className="text-sm text-gray-600 mb-2">
                {t("cart.deliveryTime")
                  .replace(
                    "{minTime}",
                    ((estimatedDistance / MAX_SPEED) * 60).toFixed(0)
                  )
                  .replace(
                    "{maxTime}",
                    ((estimatedDistance / MIN_SPEED) * 60).toFixed(0)
                  )}
              </p>
            ) : (
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600 mx-auto"></div>
                <p className="text-gray-500 mt-2">{t("common.loading")}</p>
              </div>
            )
          ) : null}
          <p className="font-semibold">
            {currentOrder.type === "DELIVERY"
              ? t("orderDetail.delivery", "Delivery")
              : t("orderDetail.pickup", "Pickup")}
          </p>
          {currentOrder.type === "DELIVERY" && currentOrder.address && (
            <div className="text-gray-600 mt-1">
              <p>{currentOrder.address.street}</p>
              <p>
                {currentOrder.address.city}, {currentOrder.address.zipCode}
              </p>
              <p>{currentOrder.address.country}</p>
            </div>
          )}
          {currentOrder.type !== "DELIVERY" && (
            <p className="text-gray-600 mt-1">
              {t("orderDetail.pickupAtRestaurant")}
            </p>
          )}
        </div>
      </div>

      {/* Payment Modal overlay - rendered when paying; hides the order detail above */}
      {isPaymentModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
          <div className="bg-white rounded-xl w-full max-w-3xl p-6 max-h-[90vh] overflow-y-auto">
            <Checkout
              cartItems={currentOrder.items as any}
              totalPrice={currentOrder.totalPrice}
              deliveryOption={
                currentOrder.type.toLowerCase() as "delivery" | "pickup"
              }
              deliveryAddress={currentOrder.address}
              deliveryFee={currentOrder.deliveryFee}
              onOrderPlaced={(newOrder) => {
                setCurrentOrder(newOrder);
                setPaymentModalOpen(false);
              }}
              onBack={() => setPaymentModalOpen(false)}
              orderId={currentOrder.id}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default OrderDetail;
